import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Icon } from "@/components/IconSprite";
import { navGroups } from "@/state/nav";
import { useUi } from "@/state/ui";
import { useAuth } from "@/state/auth";

interface Command {
  key: string; // search keywords
  title: string;
  sub: string;
  icon: string;
  kbd?: string;
  run: () => void;
  section: "Jump to" | "Actions";
}

export const CommandPalette = () => {
  const navigate = useNavigate();
  const open = useUi((s) => s.paletteOpen);
  const close = useUi((s) => s.closePalette);
  const toggleTheme = useUi((s) => s.toggleTheme);
  const logout = useAuth((s) => s.logout);

  const [query, setQuery] = useState("");
  const [active, setActive] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);

  const commands = useMemo<Command[]>(() => {
    const jump: Command[] = navGroups.flatMap((group) =>
      group.items.map((item) => ({
        key: `${item.label} ${group.label}`.toLowerCase(),
        title: item.label,
        sub: item.adv ? `${group.label} · Advanced` : group.label,
        icon: item.icon,
        section: "Jump to" as const,
        run: () => {
          navigate(item.to);
          close();
        },
      })),
    );
    const actions: Command[] = [
      {
        key: "new template create content generation",
        title: "New template",
        sub: "Content Generation",
        icon: "plus",
        kbd: "N T",
        section: "Actions",
        run: () => {
          navigate("/content-templates/new");
          close();
        },
      },
      {
        key: "add user invite admin",
        title: "Add user",
        sub: "Administration",
        icon: "user",
        kbd: "N U",
        section: "Actions",
        run: () => {
          navigate("/users");
          close();
        },
      },
      {
        key: "upload document knowledge add content",
        title: "Upload document",
        sub: "Content Library",
        icon: "upload",
        section: "Actions",
        run: () => {
          navigate("/content");
          close();
        },
      },
      {
        key: "toggle theme dark light mode",
        title: "Toggle theme",
        sub: "Switch between light and dark",
        icon: "moon",
        kbd: "⌘⇧L",
        section: "Actions",
        run: () => {
          toggleTheme();
        },
      },
      {
        key: "sign out logout end session",
        title: "Sign out",
        sub: "End your admin session",
        icon: "out",
        section: "Actions",
        run: () => {
          close();
          logout().catch(() => undefined);
        },
      },
    ];
    return [...jump, ...actions];
  }, [navigate, close, toggleTheme, logout]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return commands;
    return commands.filter((c) => (c.key + " " + c.title.toLowerCase()).includes(q));
  }, [commands, query]);

  useEffect(() => {
    if (open) {
      setQuery("");
      setActive(0);
      const t = setTimeout(() => inputRef.current?.focus(), 40);
      return () => clearTimeout(t);
    }
    return undefined;
  }, [open]);

  useEffect(() => setActive(0), [query]);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setActive((i) => (filtered.length ? (i + 1) % filtered.length : 0));
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setActive((i) => (filtered.length ? (i - 1 + filtered.length) % filtered.length : 0));
      } else if (e.key === "Enter") {
        e.preventDefault();
        filtered[active]?.run();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, filtered, active]);

  if (!open) return null;

  // Group filtered results while preserving the flat index for keyboard highlight.
  let flatIndex = -1;
  const sections: Array<"Jump to" | "Actions"> = ["Jump to", "Actions"];

  return (
    <div className="cp-ov open" onClick={(e) => e.target === e.currentTarget && close()}>
      <div className="cp">
        <div className="cp-in">
          <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9}>
            <use href="#i-search" />
          </svg>
          <input
            ref={inputRef}
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search screens, templates, and actions…"
          />
          <span className="esc">ESC</span>
        </div>
        <div className="cp-res">
          {sections.map((section) => {
            const items = filtered.filter((c) => c.section === section);
            if (!items.length) return null;
            return (
              <div key={section}>
                <div className="cp-g">{section}</div>
                {items.map((c) => {
                  flatIndex += 1;
                  const idx = flatIndex;
                  return (
                    <button
                      key={`${section}-${c.title}`}
                      className={`cp-i${idx === active ? " on" : ""}`}
                      onMouseEnter={() => setActive(idx)}
                      onClick={c.run}
                    >
                      <span className="cp-ic">
                        <Icon name={c.icon} />
                      </span>
                      <span className="cp-t">
                        <b>{c.title}</b>
                        <span>{c.sub}</span>
                      </span>
                      {c.kbd && <span className="kbd">{c.kbd}</span>}
                    </button>
                  );
                })}
              </div>
            );
          })}
          {!filtered.length && (
            <div className="empty sm">
              <div className="empty-i">
                <Icon name="search" />
              </div>
              <b>Nothing matches that</b>
              <p style={{ marginBottom: 0 }}>
                Try a screen name, a template slug, or an action like "new template".
              </p>
            </div>
          )}
        </div>
        <div className="cp-f">
          <span>
            <span className="kbd">↑</span>
            <span className="kbd">↓</span> Navigate
          </span>
          <span>
            <span className="kbd">↵</span> Open
          </span>
          <span>
            <span className="kbd">esc</span> Dismiss
          </span>
        </div>
      </div>
    </div>
  );
};

export default CommandPalette;
